import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { NorwoodQuiz } from "@/components/site/NorwoodQuiz";
import { Guarantee } from "@/components/site/Guarantee";
import { QuoteForm } from "@/components/site/QuoteForm";
import { Results } from "@/components/site/Results";
import { fullResults, results as wpResults } from "@/lib/site-data";
import { RESULTS_STORIES } from "@/lib/results-stories";
import type { CelebrityResult } from "@/lib/celebrity-results/types";
import { celebrityVideoSeo, videoObject } from "@/lib/video-seo";
import { PosterVideo } from "@/components/site/PosterVideo";
import { breadcrumbSchema, safeJsonLd } from "@/lib/structured-data-core";
import kurtWagnerPoster from "@/assets/restore/kurt-wagner-video.webp";

const GUARANTEE_BADGE =
  "/img/r/wp-2023-12-satisfation-guarantee.webp";
const COST_VIDEO =
  "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/CostInvestmenttoRegrowYourHairRESTOREHair.mp4";

function toTitle(value: string) {
  return value
    .toLowerCase()
    .replace(/(^|[\s'-])([a-z])/g, (_m, p1, p2) => p1 + p2.toUpperCase());
}

/** Like renderParagraph, but links every phrase in the list (first match of each). */
function renderParagraphLinks(text: string, links: { text: string; href: string }[]) {
  let nodes: (string | React.JSX.Element)[] = [text];
  links.forEach((l, li) => {
    nodes = nodes.flatMap((n, ni): (string | React.JSX.Element)[] => {
      if (typeof n !== "string") return [n];
      const at = n.indexOf(l.text);
      if (at < 0) return [n];
      return [
        n.slice(0, at),
        <a
          key={`${li}-${ni}`}
          href={l.href}
          className="text-[#B99244] underline underline-offset-2 hover:opacity-80"
        >
          {l.text}
        </a>,
        n.slice(at + l.text.length),
      ];
    });
  });
  return <>{nodes}</>;
}

function renderParagraph(
  text: string,
  links?: { text: string; href: string }[],
) {
  const match = links?.find((l) => text.includes(l.text));
  if (!match) return text;
  const [before, ...rest] = text.split(match.text);
  return (
    <>
      {before}
      <a
        href={match.href}
        className="text-[#B99244] underline underline-offset-2 hover:opacity-80"
      >
        {match.text}
      </a>
      {rest.join(match.text)}
    </>
  );
}

function buildStoryHeading(
  prefix: string | undefined,
  suffix: string | undefined,
  name: string,
) {
  const p = (prefix ?? "").trim();
  const s = (suffix ?? "").trim();
  const full = `${p}${p && s ? " " : ""}${s}`;
  const nameLower = name.toLowerCase();

  // When the prefix/suffix already contains the athlete's name, render that
  // full text and bold the name so we don't duplicate it.
  const idx = full.toLowerCase().indexOf(nameLower);
  if (idx >= 0) {
    const before = full.slice(0, idx);
    const matched = full.slice(idx, idx + name.length);
    const after = full.slice(idx + name.length);
    return (
      <>
        {before}
        <span className="font-bold">{matched}</span>
        {after}
      </>
    );
  }

  return (
    <>
      {p ? `${p} ` : ""}
      <span className="font-bold">{name}</span>
      {s ? ` ${s}` : ""}
    </>
  );
}

/** WordPress parity options. When set: the h1 and h2 text, the hero text under the banner, every tab panel in the HTML, the results grid of the WordPress page, the endorsement disclosure and the story links. */
export type CelebrityWpParity = { h1?: string; h2?: string; links?: { text: string; href: string }[] };

export function CelebrityResultPage({ data, wp }: { data: CelebrityResult; wp?: CelebrityWpParity }) {
  const videoSeo = celebrityVideoSeo(data);
  const [activeTab, setActiveTab] = useState(0);
  const [storyPlaying, setStoryPlaying] = useState(false);
  const storyVideoRef = useRef<HTMLVideoElement>(null);
  const [costPlaying, setCostPlaying] = useState(false);
  const tabs = data.story?.tabs ?? [];
  const displayName = `${toTitle(data.firstName)} ${toTitle(data.lastName)}`;
  const links = data.inlineLinks;

  return (
    <div className="min-h-screen bg-background font-sans">
      {videoSeo ? (
        <script type="application/ld+json">
          {JSON.stringify(videoObject(videoSeo))}
        </script>
      ) : null}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: safeJsonLd(
            breadcrumbSchema([
              { name: "Results", path: "/results/" },
              { name: displayName, path: `/results/${data.slug}/` },
            ]),
          ),
        }}
      />
      <Header />
      <main data-cta-location="results">
        {/* HERO */}
        <section
          className="relative overflow-hidden"
          style={{
            backgroundColor: data.heroBackgroundColor ?? "#FBF8EE",
            minHeight: data.heroBannerImage
              ? undefined
              : (data.heroMinHeight ?? "640px"),
          }}
        >
          {data.heroBannerImage ? (
            <>
              <picture>
                {data.heroBannerImageMobile ? (
                  <source
                    media="(min-width: 1024px)"
                    srcSet={data.heroBannerImage.src}
                  />
                ) : null}
                <img
                  src={
                    data.heroBannerImageMobile
                      ? data.heroBannerImageMobile.src
                      : data.heroBannerImage.src
                  }
                  alt={
                    data.heroBannerImageMobile
                      ? data.heroBannerImageMobile.alt
                      : data.heroBannerImage.alt
                  }
                  width={data.heroBannerImageMobile ? 800 : 1920}
                  height={data.heroBannerImageMobile ? 879 : 800}
                  className={
                    data.heroBannerImageMobile
                      ? (data.heroBannerImageMobileClassName ??
                        "block w-full aspect-[800/879] object-contain lg:aspect-[12/5]")
                      : "block h-auto w-full object-contain"
                  }
                  loading="eager"
                />
              </picture>
              <h1 className="sr-only">{wp?.h1 ?? data.seoH1 ?? `${displayName}'s Hair Restoration Story with RESTORE`}</h1>
              {wp?.h2 ? <h2 className="sr-only">{wp.h2}</h2> : null}
              {wp ? (
                <div className="mx-auto max-w-3xl px-5 py-8 text-center">
                  {data.roleLines.map((line) => (
                    <p key={line} className="text-sm text-foreground/80">
                      {line}
                    </p>
                  ))}
                  {data.headlineLines.length > 0 && (
                    <p className="mt-4 text-2xl font-bold text-foreground">{data.headlineLines.join(" ")}</p>
                  )}
                  <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
                    {data.intro.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </div>
              ) : null}
              {/* The banner artwork shows this text as an image. Same words in the HTML.
                  Only pages that set seoH1 get it. */}
              {data.seoH1 ? (
                <div className="sr-only">
                  {data.roleLines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                  {data.headlineLines.length > 0 && <h2>{data.headlineLines.join(" ")}</h2>}
                  {data.intro.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              ) : null}
            </>
          ) : data.heroLayout === "light-split" ? (

            <div className="mx-auto w-full max-w-[1280px] px-5 lg:px-5">
              <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-4">
                {/* Copy */}
                <div className="pt-10 lg:pt-14">
                  {data.headlineLines.length > 0 && (
                    <h1 className="text-[38px] font-bold leading-[1.05] text-black md:text-[52px]">
                      {data.headlineLines.join(" ")}
                    </h1>
                  )}
                  <h2 className="mt-2 text-[30px] font-bold leading-tight text-black md:text-[38px]">
                    {displayName}
                  </h2>
                  <div className="mt-5 space-y-1 text-lg text-black/80">
                    {data.roleLines.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                  <a
                    href="#my-story"
                    className="mt-8 inline-block bg-[#D7B970] px-7 py-5 text-base font-bold text-black transition hover:opacity-90"
                  >
                    {data.heroCtaLabel ?? "See My Story"}
                  </a>
                  {data.heroActionThumb ? (
                    <img
                      src={data.heroActionThumb.src}
                      alt={data.heroActionThumb.alt}
                      className="mt-8 w-[238px] max-w-full object-contain"
                      loading="eager"
                    />
                  ) : null}
                </div>
                {/* Portrait */}
                <div className="flex justify-center lg:justify-end">
                  {data.posterImage ? (
                    <img
                      src={data.posterImage.src}
                      alt={data.posterImage.alt}
                      className="w-full max-w-[547px] object-contain"
                      loading="eager"
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : data.heroLayout === "diagonal-right" ? (
            <>
              {/* Original RESTORE layout: wide left action photo, cream right
                  panel clipped on a diagonal, portrait bottom-aligned. */}
              {data.heroBackground ? (
                <div
                  className="absolute inset-y-0 left-0 hidden bg-cover lg:block"
                  style={{
                    width: data.heroBackgroundWidth ?? "78%",
                    backgroundImage: `url(${data.heroBackground})`,
                    backgroundPosition: data.heroBackgroundPosition ?? "center",
                  }}
                  aria-hidden="true"
                />
              ) : null}
              {/* Dark scrim behind the copy */}
              <div
                 className="absolute inset-y-0 left-0 hidden w-[44%] lg:block"
                style={{
                  background:
                     "linear-gradient(90deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 42%, rgba(0,0,0,0.18) 72%, rgba(0,0,0,0) 100%)",
                }}
                aria-hidden="true"
              />

              {/* Right-hand cream panel with the diagonal left edge */}
              <div
                className="absolute inset-y-0 right-0 z-[5] hidden lg:block"
                style={{
                   width: "50%",
                  backgroundColor: data.heroBackgroundColor ?? "#FFF9F1",
                   clipPath: "polygon(0 0, 100% 0, 100% 100%, 36% 100%)",
                }}
                aria-hidden="true"
              />

              <Link
                to="/restore-pledge/"
                className="absolute right-4 top-4 z-30 hidden lg:block"
              >
                <img loading="lazy" decoding="async"
                  src={GUARANTEE_BADGE}
                  alt="RESTORE Hair Satisfaction Guarantee"
                  className="h-24 w-24 object-contain"
                />
              </Link>

              {/* Portrait + before/after, anchored to the bottom-right of the hero */}
              {data.posterImage ? (
                <img
                  src={data.posterImage.src}
                  alt={data.posterImage.alt}
                  className="absolute bottom-0 z-10 hidden object-contain object-bottom lg:block"
                  style={{
                    right: data.posterRightOffset ?? "11%",
                    height: data.posterHeight ?? "86%",
                    width: data.posterWidth ?? "auto",
                  }}
                  loading="eager"
                />
              ) : null}
              {data.heroBeforeAfter ? (
                <img loading="lazy" decoding="async"
                  src={data.heroBeforeAfter.src}
                  alt={data.heroBeforeAfter.alt}
                  className="absolute bottom-5 z-20 hidden border border-black/10 bg-white object-contain shadow-sm lg:block"
                  style={{
                    width: data.heroBeforeAfterWidth ?? "314px",
                    right: data.heroBeforeAfterRight ?? "20px",
                  }}
                />
              ) : null}

               <div className="relative z-10 hidden lg:grid lg:grid-cols-[minmax(320px,30%)_1fr] lg:items-stretch">
                {/* Copy */}
                <div
                  className="px-6 py-10 text-white lg:px-10 lg:py-14"
                  style={{ minHeight: data.heroMinHeight ?? "700px" }}
                >
                  <h1 className="text-4xl font-bold uppercase leading-none tracking-tight md:text-5xl">
                    <span className="block text-[#D7B970]">{data.firstName}</span>
                    <span className="block">{data.lastName}</span>
                  </h1>
                  <div className="mt-5 space-y-1 text-sm md:text-[15px]">
                    {data.roleLines.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                  {data.headlineLines.length > 0 && (
                    <h2 className="mt-7 text-2xl font-bold leading-tight md:text-3xl">
                      {data.headlineLines.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </h2>
                  )}
                  <div className="mt-6 max-w-sm space-y-4 text-sm leading-relaxed">
                    {data.intro.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </div>
                <div />
              </div>

              {/* Mobile: copy over the action photo */}
              <div className="relative lg:hidden">
                {data.heroBackground ? (
                  <div
                    className="absolute inset-0 bg-cover"
                    style={{
                      backgroundImage: `url(${data.heroBackground})`,
                      backgroundPosition: data.heroBackgroundPosition ?? "center",
                    }}
                    aria-hidden="true"
                  />
                ) : null}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.66) 55%, rgba(0,0,0,0.35) 100%)",
                  }}
                  aria-hidden="true"
                />
                <div className="relative z-10 px-6 py-10 text-white">
                  <h1 className="text-[40px] font-bold uppercase leading-[0.95] tracking-tight">
                    <span className="block text-[#D7B970]">{data.firstName}</span>
                    <span className="block">{data.lastName}</span>
                  </h1>
                  <div className="mt-5 space-y-1 text-[17px]">
                    {data.roleLines.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                  {data.headlineLines.length > 0 && (
                    <h2 className="mt-7 text-[28px] font-bold leading-tight">
                      {data.headlineLines.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </h2>
                  )}
                  <div className="mt-6 space-y-4 text-[17px] leading-relaxed">
                    {data.intro.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile: light panel with the portrait, badge and before/after */}
              <div
                className="relative lg:hidden"
                style={{ backgroundColor: data.heroBackgroundColor ?? "#FFF9F1" }}
              >
                <div className="absolute inset-0 bg-white" style={{ clipPath: "polygon(3% 0, 100% 0, 100% 100%, 0 100%)" }} aria-hidden="true" />
                <div className="relative z-10 px-4 pb-6 pt-6">
                  <div className="flex items-start justify-end">
                    <Link to="/restore-pledge/">
                      <img loading="lazy" decoding="async"
                        src={GUARANTEE_BADGE}
                        alt="RESTORE Hair Satisfaction Guarantee"
                        className="h-24 w-24 object-contain"
                      />
                    </Link>
                  </div>
                  {data.posterImage ? (
                    <img
                      src={data.posterImage.src}
                      alt={data.posterImage.alt}
                      className="-mt-20 w-[72%] max-w-[300px] object-contain"
                      loading="eager"
                    />
                  ) : null}
                  {data.heroBeforeAfter ? (
                    <img loading="lazy" decoding="async"
                      src={data.heroBeforeAfter.src}
                      alt={data.heroBeforeAfter.alt}
                      className="relative z-20 mx-auto -mt-6 w-[80%] border border-black/10 bg-white object-contain shadow-sm"
                    />
                  ) : null}
                </div>
              </div>

            </>
          ) : (
            <>
              {/* Default layout: action photo with diagonal crop on the left,
                  portrait bottom-aligned on the right. */}
              {data.heroBackground ? (
                <div
                  className={`absolute inset-y-0 left-0 hidden bg-cover lg:block ${
                    data.posterImage ? "w-[64%]" : "w-full"
                  }`}
                  style={{
                    backgroundImage: `url(${data.heroBackground})`,
                    backgroundPosition: data.heroBackgroundPosition ?? "center",
                    ...(data.posterImage
                      ? { clipPath: "polygon(0 0, 100% 0, 84% 100%, 0 100%)" }
                      : {}),
                  }}
                  aria-hidden="true"
                />
              ) : null}
              {/* Dark scrim behind the copy */}
              <div
                className={`absolute inset-y-0 left-0 hidden lg:block ${
                  data.posterImage ? "w-[64%]" : "w-full"
                }`}
                style={{
                  ...(data.posterImage
                    ? { clipPath: "polygon(0 0, 100% 0, 84% 100%, 0 100%)" }
                    : {}),
                  background:
                    "linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 34%, rgba(0,0,0,0.15) 58%, rgba(0,0,0,0) 78%)",
                }}
                aria-hidden="true"
              />

              <Link
                to="/restore-pledge/"
                className="absolute right-4 top-4 z-30 hidden lg:block"
              >
                <img loading="lazy" decoding="async"
                  src={GUARANTEE_BADGE}
                  alt="RESTORE Hair Satisfaction Guarantee"
                  className="h-24 w-24 object-contain"
                />
              </Link>

              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[minmax(320px,34%)_1fr] lg:items-stretch">
                {/* Copy */}
                <div className="bg-black/80 px-6 py-10 text-white lg:min-h-[640px] lg:bg-transparent lg:px-10 lg:py-14">
                  <h1 className="text-4xl font-bold uppercase leading-none tracking-tight md:text-5xl">
                    <span className="block text-[#D7B970]">{data.firstName}</span>
                    <span className="block">{data.lastName}</span>
                  </h1>
                  <div className="mt-5 space-y-1 text-sm md:text-[15px]">
                    {data.roleLines.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                  {data.headlineLines.length > 0 && (
                    <h2 className="mt-7 text-2xl font-bold leading-tight md:text-3xl">
                      {data.headlineLines.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </h2>
                  )}
                  <div className="mt-6 max-w-sm space-y-4 text-sm leading-relaxed">
                    {data.intro.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </div>

                {/* Endorser portrait on the right, before & after bottom right */}
                <div className={`relative flex min-h-[360px] items-end lg:min-h-[640px] ${data.posterJustify === "center" ? "justify-center" : "justify-end"}`}>
                  {data.heroBackground ? (
                    <div
                      className="absolute inset-0 bg-cover lg:hidden"
                      style={{
                        backgroundImage: `url(${data.heroBackground})`,
                        backgroundPosition: data.heroBackgroundPosition ?? "center",
                      }}
                      aria-hidden="true"
                    />
                  ) : null}
                  {data.posterImage ? (
                    <img
                      src={data.posterImage.src}
                      alt={data.posterImage.alt}
                      className="relative z-10 w-auto self-end object-contain"
                      style={{
                        ...(data.posterHeight
                          ? { height: data.posterHeight }
                          : { maxHeight: data.posterMaxHeight ?? "640px" }),
                        marginRight: data.posterRightOffset ?? "4%",
                      }}
                      loading="eager"
                    />
                  ) : null}
                  {data.heroBeforeAfter ? (
                    <img loading="lazy" decoding="async"
                      src={data.heroBeforeAfter.src}
                      alt={data.heroBeforeAfter.alt}
                      className="absolute bottom-0 right-0 z-20 w-40 border border-black/10 bg-white object-contain shadow-sm md:w-64 lg:w-[270px]"
                    />
                  ) : null}
                </div>
              </div>
            </>
          )}
        </section>


        {/* NORWOOD PICKER. On a page with a story video it moves below the
            story: Google indexes a video only when it is the page's main
            content, and the picker pushed it about 2,000px down on phones. */}
        {!data.hideNorwoodQuiz && !data.story?.video && <NorwoodQuiz />}


        {/* INTRO (light-split hero keeps the copy below the picker) */}
        {data.heroLayout === "light-split" && data.intro.length > 0 ? (
          <section className="bg-white py-14">
            <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-x-12 gap-y-6 px-4 text-base leading-relaxed text-muted-foreground md:grid-cols-2 lg:px-8">
              <div className="space-y-4 [&>p:first-child]:first-letter:mr-1 [&>p:first-child]:first-letter:float-left [&>p:first-child]:first-letter:font-serif [&>p:first-child]:first-letter:text-[52px] [&>p:first-child]:first-letter:leading-[42px] [&>p:first-child]:first-letter:text-foreground">
                {data.intro.slice(0, 1).map((p, i) => (
                  <p key={i}>{renderParagraph(p, links)}</p>
                ))}
              </div>
              <div className="space-y-6">
                {data.intro.slice(1).map((p, i) => (
                  <p key={i}>{renderParagraph(p, links)}</p>
                ))}
              </div>
            </div>
          </section>
        ) : null}


        {/* STORY */}
        {data.story ? (
          <section className={`bg-white ${data.story.dark ? "" : "py-14"}`}>
            {(() => {
              const videoBlock = (
                <>
                  {data.story.video && data.story.image ? (
                    // The <video> is in the server HTML (Google's video
                    // indexing needs it on load) and lies over the story
                    // image, which keeps setting the size.
                    <div
                      className={`relative mx-auto mt-8 block w-full ${data.story.dark ? "max-w-[1200px]" : data.story.videoFullWidth ? "" : "max-w-4xl"}`}
                    >
                      <img
                        src={data.story.image.src}
                        alt={data.story.image.alt}
                        className="w-full object-contain"
                        loading="lazy"
                      />
                      <PosterVideo
                        ref={storyVideoRef}
                        src={data.story.video}
                        poster={data.story.image.src}
                        title={`${displayName}'s story`}
                        active={storyPlaying}
                        className={storyPlaying ? "bg-black" : ""}
                      />
                      {storyPlaying ? null : (
                        <button
                          type="button"
                          onClick={() => {
                            setStoryPlaying(true);
                            void storyVideoRef.current?.play().catch(() => {});
                          }}
                          className="group absolute inset-0 block w-full"
                          aria-label={`Play ${displayName}'s story video`}
                        >
                          <span className="absolute inset-0 flex items-center justify-center">
                            <span
                              className={`flex items-center justify-center rounded-full bg-[#D7B970] shadow-lg transition group-hover:scale-105 ${
                                data.story.dark ? "h-24 w-24 md:h-28 md:w-28" : "h-20 w-20"
                              }`}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                className="ml-1 h-9 w-9 fill-black"
                                aria-hidden="true"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </span>
                          </span>
                        </button>
                      )}
                    </div>
                  ) : data.story.video ? (
                    storyPlaying ? (
                      <video
                        src={data.story.video}
                        poster={data.story.image?.src}
                        controls
                        autoPlay
                        playsInline
                        className={`mx-auto mt-8 w-full ${data.story.dark ? "max-w-[1200px]" : data.story.videoFullWidth ? "" : "max-w-4xl"}`}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setStoryPlaying(true)}
                        className={`group relative mx-auto mt-8 block w-full ${data.story.dark ? "max-w-[1200px]" : data.story.videoFullWidth ? "" : "max-w-4xl"}`}
                        aria-label={`Play ${displayName}'s story video`}
                      >
                        {data.story.image ? (
                          <img
                            src={data.story.image.src}
                            alt={data.story.image.alt}
                            className="w-full object-contain"
                            loading="lazy"
                          />
                        ) : null}
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span
                            className={`flex items-center justify-center rounded-full bg-[#D7B970] shadow-lg transition group-hover:scale-105 ${
                              data.story.dark ? "h-24 w-24 md:h-28 md:w-28" : "h-20 w-20"
                            }`}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="ml-1 h-9 w-9 fill-black"
                              aria-hidden="true"
                            >
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </span>
                        </span>
                      </button>
                    )
                  ) : data.story.image ? (
                    <img
                      src={data.story.image.src}
                      alt={data.story.image.alt}
                      className="mx-auto mt-8 w-full max-w-4xl object-contain"
                      loading="lazy"
                    />
                  ) : null}
                </>
              );

              const heading = (
                <h2
                  className={`text-center text-2xl font-normal md:text-[34px] ${
                    data.story.dark ? "text-white" : "text-foreground"
                  }`}
                >
                  {buildStoryHeading(
                    data.story.headingPrefix,
                    data.story.headingSuffix,
                    displayName,
                  )}
                </h2>
              );

              return (
                <>
                  {data.story.dark ? (
                    <div className="bg-black py-14 text-white">
                      <div className="mx-auto w-full max-w-[1400px] px-4 lg:px-8">
                        {heading}
                        {videoBlock}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`mx-auto w-full ${
                        data.story.videoFullWidth ? "max-w-[1400px] px-0" : "max-w-6xl px-4 lg:px-8"
                      }`}
                    >
                      {heading}
                      {videoBlock}
                    </div>
                  )}

                  <div
                    className={`mx-auto w-full max-w-6xl px-4 lg:px-8 ${
                      data.story.dark ? "py-14" : ""
                    }`}
                  >
                    {data.story.pullQuote ? (
                      <figure className="mx-auto max-w-3xl text-center">
                        <div className="relative px-10 py-8 md:px-16 md:py-10">
                          <span
                            aria-hidden="true"
                            className="absolute left-0 top-0 h-10 w-10 border-l-2 border-t-2 border-[#D7B970]"
                          />
                          <span
                            aria-hidden="true"
                            className="absolute bottom-0 right-0 h-10 w-10 border-b-2 border-r-2 border-[#D7B970]"
                          />
                          <blockquote className="whitespace-pre-line text-xl italic leading-snug text-[#333] md:text-[30px]">
                            {data.story.pullQuote.text}
                          </blockquote>
                          {data.story.pullQuote.attribution ? (
                            <figcaption className="mt-6 text-base font-bold text-black">
                              –{data.story.pullQuote.attribution}
                            </figcaption>
                          ) : null}
                        </div>
                      </figure>
                    ) : null}

                    {data.story.articleHeading ? (
                      <h3 className="mx-auto mt-12 max-w-3xl whitespace-pre-line text-center text-2xl font-normal text-foreground md:text-[36px] md:leading-tight">
                        {data.story.articleHeading}
                      </h3>
                    ) : null}

                    {data.story.paragraphs?.length ? (
                      data.story.articleTwoColumn ? (
                        <div className="mx-auto mt-8 max-w-5xl gap-10 text-base leading-relaxed text-muted-foreground md:columns-2">
                          {data.story.paragraphs.map((p, i) => (
                            <p
                              key={i}
                              className={`mb-4 break-inside-avoid ${
                                i === 0
                                  ? "first-letter:float-left first-letter:mr-2 first-letter:text-[64px] first-letter:font-bold first-letter:leading-[0.85] first-letter:text-black"
                                  : ""
                              }`}
                            >
                              {wp?.links ? renderParagraphLinks(p, wp.links) : renderParagraph(p, links)}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <div className="mx-auto mt-8 max-w-4xl space-y-4 text-base leading-relaxed text-muted-foreground">
                          {data.story.paragraphs.map((p, i) => (
                            <p key={i}>{wp?.links ? renderParagraphLinks(p, wp.links) : renderParagraph(p, links)}</p>
                          ))}
                        </div>
                      )
                    ) : null}

                    {tabs.length > 0 ? (
                      <div className="mx-auto mt-10 max-w-4xl">
                        <div className="flex flex-wrap gap-2 border-b border-border">
                          {tabs.map((tab, i) => (
                            <button
                              key={tab.label}
                              type="button"
                              onClick={() => setActiveTab(i)}
                              className={`px-5 py-3 text-base font-bold transition ${
                                activeTab === i
                                  ? "border-b-2 border-[#D7B970] text-foreground"
                                  : "text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                        {wp ? (
                          tabs.map((tab, ti) => (
                            <div
                              key={tab.label}
                              hidden={activeTab !== ti}
                              className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground"
                            >
                              {tab.paragraphs.map((p, i) => (
                                <p key={i}>{renderParagraph(p, links)}</p>
                              ))}
                            </div>
                          ))
                        ) : (
                        <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
                          {tabs[activeTab]?.paragraphs.map((p, i) => (
                            <p key={i}>{renderParagraph(p, links)}</p>
                          ))}
                        </div>
                        )}
                      </div>
                    ) : null}

                    <div className="mt-10 text-center">
                      <Link
                        to="/schedule-a-consult/"
                        className="inline-block bg-[#D7B970] px-8 py-4 text-base font-bold uppercase tracking-wide text-black transition hover:opacity-90"
                      >
                        Schedule a Consult
                      </Link>
                    </div>
                  </div>
                </>
              );
            })()}
          </section>
        ) : null}

        {/* NORWOOD PICKER, below the story video (see above) */}
        {!data.hideNorwoodQuiz && data.story?.video ? <NorwoodQuiz /> : null}

        {/* GALLERY */}
        {data.gallery ? (
          <section
            id="my-story"
            className={`${
              data.heroLayout === "light-split"
                ? "bg-white pb-6 pt-0"
                : data.gallery.quoteStyle === "brackets"
                  ? "bg-white pb-14 pt-0"
                  : "bg-[#F5F5F5] py-14"
            }`}
          >
            {data.gallery.quoteStyle === "brackets" && data.gallery.quote ? (
              <div className="bg-[#F5F5F5] px-4 py-12 md:py-16">
                <figure className="relative mx-auto w-full max-w-[1240px] px-8 py-10 text-center md:px-16">
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-0 h-12 w-16 border-l-2 border-t-2 border-[#C9A227]"
                  />
                  <span
                    aria-hidden="true"
                    className="absolute bottom-0 right-0 h-12 w-16 border-b-2 border-r-2 border-[#C9A227]"
                  />
                  <blockquote className="mx-auto max-w-3xl text-2xl italic leading-snug text-[#333] md:text-[34px]">
                    {data.gallery.quote}
                  </blockquote>
                  {data.gallery.quoteAttribution ? (
                    <figcaption className="mt-6 text-sm font-bold text-black">
                      {data.gallery.quoteAttribution}
                    </figcaption>
                  ) : null}
                </figure>
              </div>
            ) : null}
            <div
              className={`mx-auto w-full max-w-6xl px-4 lg:px-8 ${
                data.gallery.quoteStyle === "brackets" ? "pt-12" : ""
              }`}
            >
              {data.heroLayout === "light-split" || data.gallery.hideHeading ? null : (
                <h2 className="text-center text-xl font-normal text-foreground md:text-[28px]">
                  {data.gallery.heading ?? (
                    <>
                      See <span className="font-bold">{displayName}'s</span>{" "}
                      Before &amp; After Pictures
                    </>
                  )}
                </h2>
              )}


              {data.gallery.quoteStyle !== "brackets" &&
              (data.gallery.featureImage || data.gallery.quote) ? (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2">
                  {data.gallery.featureImage ? (
                    <img
                      src={data.gallery.featureImage.src}
                      alt={data.gallery.featureImage.alt}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : null}
                  {data.gallery.quote ? (
                    <figure className="flex flex-col justify-center bg-[#575757] px-8 py-10 text-white md:px-12">
                      <blockquote className="text-2xl capitalize leading-snug md:text-[34px]">
                        {data.gallery.quote}
                      </blockquote>
                      {data.gallery.quoteAttribution ? (
                        <figcaption className="mt-6 text-lg font-bold">
                          {data.gallery.quoteAttribution}
                        </figcaption>
                      ) : null}
                    </figure>
                  ) : null}
                </div>
              ) : null}

              {data.gallery.images.length > 0 ? (
                data.heroLayout === "light-split" ? (
                  <div className="mt-10 grid grid-cols-2 items-start gap-4 md:grid-cols-3">
                    {data.gallery.images.map((img) => (
                      <img
                        key={img.src}
                        src={img.src}
                        alt={img.alt}
                        className="h-[150px] w-full object-cover object-top md:h-[210px]"
                        loading="lazy"
                      />
                    ))}
                  </div>
                ) : (
                  <GalleryNaturalRow images={data.gallery.images} />
                )
              ) : null}

            </div>
          </section>
        ) : null}

        {/* Q&A */}
        {data.qa ? (
          <section className="bg-white pt-14">
            <div className="mx-auto w-full max-w-4xl px-4 lg:px-8">
              <h2 className="text-center text-xl font-normal text-foreground md:text-[24px]">
                {data.qa.heading ?? (
                  <>
                    <span className="font-bold">{displayName}</span>{" "}
                    Answers Questions About His Hair Transplant Procedure
                  </>
                )}
              </h2>
              <div className="mt-10 divide-y divide-border">
                {data.qa.items.map((item, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-1 gap-6 py-9 last:pb-0 md:grid-cols-[135px_1fr] md:gap-12"
                  >
                    {item.image ? (
                    <img
                        src={item.image.src}
                        alt={item.image.alt}
                        className={`w-[135px] max-w-full object-top ${
                          data.slug === "zach-johnson" ||
                          data.slug === "matt-forte"
                            ? "h-auto object-contain md:h-[135px]"
                            : data.slug === "jim-cornelison" ||
                          data.slug === "kevin-streelman"
                            ? "h-[135px] object-contain"
                            : "h-[135px] object-cover"
                        }`}
                        loading="lazy"
                      />
                    ) : (
                      <div />
                    )}
                    <div>
                      <div className="flex gap-4">
                        <span className="select-none font-serif text-2xl leading-none text-[#B08D4F]">
                          Q<span className="ml-1 font-sans font-light">/</span>
                        </span>
                        <h3 className="mt-1 text-[15px] font-bold italic leading-snug text-[#B08D4F]">
                          {item.question}
                        </h3>
                      </div>
                      <div className="mt-5 flex gap-4">
                        <span className="select-none font-serif text-2xl leading-none text-foreground">
                          A<span className="ml-1 font-sans font-light">/</span>
                        </span>
                        <p className="mt-1 text-[15px] leading-relaxed text-muted-foreground">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {/* REAL CLIENTS, REAL RESULTS */}
        {data.hideResultsGrid ? null : (
        <Results
          items={wp ? wpResults : fullResults.slice(0, 9)}
          initialCount={9}
          showDisclaimer={Boolean(wp)}
          nameTag={wp ? "h2" : "div"}
          columns={3}
          eyebrow="Before & After Pictures of Hair Transplants"
          heading={
            <span className="block text-[30px] leading-none font-normal">
              Real Clients,{" "}
            </span>
          }
          subheading={
            <span className="block text-[30px] leading-none font-bold">
              Real Results
            </span>
          }
        />
        )}
        {/* YOU'LL LOVE THE NEW YOU — GUARANTEED BANNER */}
        {data.guaranteeBanner ? (
          <section
            className={`relative bg-[#111] bg-cover bg-center bg-no-repeat ${
              data.hideGuaranteeBannerMobile ? "hidden md:block" : ""
            } ${
              data.guaranteeBanner.textPosition === "bottom"
                ? "min-h-[1100px] md:min-h-[540px] bg-[top] md:bg-[50%_50%]"
                : ""
            }`}
            style={{
              borderTop: "8px solid #D7B970",
              borderBottom: "8px solid #D7B970",
              backgroundImage: `url(${data.guaranteeBanner.image.src})`,
              backgroundPosition: data.guaranteeBanner.imagePosition ?? "50% 50%",
            }}
            aria-label="Satisfaction guarantee"
          >
            {data.guaranteeBanner.textPosition === "bottom" ? (
              <div
                className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/50 to-transparent"
                aria-hidden="true"
              />
            ) : null}
            {data.guaranteeBanner.textPosition === "bottom" ? (
              <div className="absolute inset-0 flex flex-col justify-end pb-14 md:pb-24">
                <div className="mx-auto grid w-full max-w-[1456px] grid-cols-1 gap-8 px-4 md:grid-cols-2 md:gap-12 lg:px-12">
                  <div className="flex flex-col justify-end px-6 pb-10 md:px-10 md:pb-14">
                    <h3 className="text-[34px] font-light uppercase leading-[1.05] tracking-tight text-white md:text-[54px]">
                      You'll Love
                      <span className="block font-extrabold">The New You</span>
                    </h3>
                    <p
                      className="mt-3 text-[36px] italic leading-none md:text-[52px]"
                      style={{ color: "#D7B970", fontFamily: "Georgia, serif" }}
                    >
                      Guaranteed.
                    </p>
                  </div>
                  <div className="flex flex-col justify-end md:pl-8">
                    <p className="max-w-[480px] text-base leading-relaxed text-white md:text-lg">
                      Your satisfaction is our #1 priority — and we stand behind our quality
                      and expertise 100%. If for any reason you're not happy with the results
                      of your RESTORE™ F.U.E. procedure, we'll perform an additional procedure
                      at no cost to you.
                    </p>
                    <Link to="/restore-pledge/" className="mt-8 inline-block">
                      <img
                        src={GUARANTEE_BADGE}
                        alt="RESTORE Hair Satisfaction Guarantee"
                        className="w-[200px] object-contain md:w-[294px]"
                        loading="lazy"
                      />
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mx-auto grid w-full max-w-[1456px] grid-cols-1 gap-8 px-4 md:grid-cols-2 md:gap-12 lg:px-12 relative items-center py-14 md:py-24">
                <div className="flex flex-col justify-end px-6 py-10 md:px-10 md:py-14">
                  <h3 className="text-[34px] font-light uppercase leading-[1.05] tracking-tight text-white md:text-[54px]">
                    You'll Love
                    <span className="block font-extrabold">The New You</span>
                  </h3>
                  <p
                    className="mt-3 text-[36px] italic leading-none md:text-[52px]"
                    style={{ color: "#D7B970", fontFamily: "Georgia, serif" }}
                  >
                    Guaranteed.
                  </p>
                </div>
                <div className="flex flex-col justify-end md:pl-8">
                  <p className="max-w-[480px] text-base leading-relaxed text-white md:text-lg">
                    Your satisfaction is our #1 priority — and we stand behind our quality
                    and expertise 100%. If for any reason you're not happy with the results
                    of your RESTORE™ F.U.E. procedure, we'll perform an additional procedure
                    at no cost to you.
                  </p>
                  <Link to="/restore-pledge/" className="mt-8 inline-block">
                    <img
                      src={GUARANTEE_BADGE}
                      alt="RESTORE Hair Satisfaction Guarantee"
                      className="w-[200px] object-contain md:w-[294px]"
                      loading="lazy"
                    />
                  </Link>
                </div>
              </div>
            )}
          </section>
        ) : (
          <Guarantee variant="results" />
        )}


        {/* HOW MUCH WILL IT COST */}
        {data.hideCostSection ? null : (
        <section className="bg-[#F5F5F5] py-16 md:py-24">
          <div className="mx-auto w-full max-w-7xl px-4 lg:px-12">
            <p className="hidden text-left text-sm font-bold text-foreground lg:block">
              How Much Will It Cost ?
            </p>
            <div className="mt-4 grid grid-cols-1 items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
              {/* Copy */}
              <div>
                <div className="flex items-start gap-5">
                  <Link to="/restore-pledge/" className="shrink-0">
                    <img
                      src={GUARANTEE_BADGE}
                      alt="RESTORE Hair Satisfaction Guarantee"
                      className="w-[120px] object-contain md:w-[150px]"
                      loading="lazy"
                    />
                  </Link>
                  <div>
                    <p className="text-center text-base font-bold text-foreground lg:hidden">
                      How Much Will It Cost?
                    </p>
                    <h3 className="text-2xl font-normal leading-tight text-foreground md:text-[30px]">
                      Invest in yourself
                    </h3>
                    <h3 className="text-2xl font-bold leading-tight text-foreground md:text-[30px]">
                      Save $1,000 on your F.U.E. Procedure
                    </h3>
                  </div>
                </div>
                <p className="mt-6 max-w-[540px] text-base leading-relaxed text-muted-foreground md:text-lg">
                  A full head of hair can pay off for a lifetime.
                  <br />
                  Every patient experiences different levels of hair loss. For that
                  reason, please fill out the form below so we can deliver a quick,
                  highly personalized, no-pressure, 100% free quote that is based on
                  your very specific set of needs. We also have payment plans
                  available which require no money down with payments starting
                  around $138 per month.
                </p>
                <Link
                  to="/quick-quote-part-1/"
                  className="mt-8 inline-block bg-[#D7B970] px-8 py-4 text-base font-bold text-black transition hover:opacity-90"
                >
                  Get A Quick Quote
                </Link>
              </div>

              {/* Video */}
              <div>
                {costPlaying ? (
                  <video
                    src={COST_VIDEO}
                    poster={kurtWagnerPoster}
                    controls
                    autoPlay
                    playsInline
                    className="w-full"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setCostPlaying(true)}
                    className="group relative block w-full"
                    aria-label="Play video: The Cost Investment to Regrow Your Hair"
                  >
                    <img
                      src={kurtWagnerPoster}
                      alt="Kurt Wagner, RESTORE Hair client, on his boat"
                      className="w-full object-cover"
                      loading="lazy"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#D7B970] shadow-lg transition group-hover:scale-105 md:h-28 md:w-28">
                        <svg
                          viewBox="0 0 24 24"
                          className="ml-1 h-8 w-8 fill-black md:h-12 md:w-12"
                          aria-hidden="true"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
        )}

        <MoreStories slug={data.slug} />

        {data.hideQuoteForm ? null : <QuoteForm />}
      </main>
      <Footer />
    </div>
  );
}

/** Renders gallery images at a shared height with widths proportional to each
 *  image's natural aspect ratio (matching the original endorser pages). */
function GalleryNaturalRow({
  images,
}: {
  images: { src: string; alt: string }[];
}) {
  const [aspects, setAspects] = useState<Record<string, number>>({});
  const total = images.reduce(
    (sum, img) => sum + (aspects[img.src] ?? 1),
    0
  );
  return (
    <div className="mt-10 flex flex-col items-center gap-4 md:flex-row md:items-start md:justify-center">
      {images.map((img) => {
        const aspect = aspects[img.src] ?? 1;
        return (
          <div
            key={img.src}
            className="w-full md:w-auto"
            style={{ flexGrow: aspect / total, flexBasis: 0 }}
          >
            <img
              src={img.src}
              alt={img.alt}
              className="h-auto w-full object-top"
              loading="lazy"
              ref={(el) => {
                if (el && el.complete && el.naturalWidth > 0) {
                  const ratio = el.naturalWidth / el.naturalHeight;
                  setAspects((prev) =>
                    prev[img.src] === ratio
                      ? prev
                      : { ...prev, [img.src]: ratio }
                  );
                }
              }}
              onLoad={(e) => {
                const el = e.currentTarget;
                if (el.naturalWidth > 0 && el.naturalHeight > 0) {
                  const ratio = el.naturalWidth / el.naturalHeight;
                  setAspects((prev) =>
                    prev[img.src] === ratio
                      ? prev
                      : { ...prev, [img.src]: ratio }
                  );
                }
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

// Links to the other results stories. Before this block each story had one
// internal link, from /results/ (Ahrefs, 3 Oct 2026).
function MoreStories({ slug }: { slug: string }) {
  const others = RESULTS_STORIES.filter((s) => s.href !== `/results/${slug}/`);
  return (
    <section className="bg-background py-10">
      <div className="mx-auto max-w-5xl px-4 text-center lg:px-8">
        <h2 className="text-2xl font-bold text-foreground">More Client Stories</h2>
        <nav aria-label="More client stories" className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2">
          {others.map((s) => (
            <a key={s.href} href={s.href} className="text-primary underline underline-offset-4 hover:no-underline">
              {s.name}
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
}
